import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function formatApiError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.must_change_password) {
        navigate('/change-password');
      } else if (data.role === 'ceo') {
        navigate('/dashboard');
      } else {
        navigate(`/division/${data.division_id}`);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F7F5F2' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 data-testid="app-title" className="text-2xl font-bold tracking-tight" style={{ color: '#1B2A4A' }}>
            Les Concierges Group
          </h1>
          <p className="mt-1 text-sm" style={{ color: '#6B7280' }}>Performance Operating System</p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="bg-white rounded-lg shadow-sm border p-6 space-y-5"
          style={{ borderColor: '#E5E1DC' }}
        >
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm py-2 px-3 rounded-md" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }}>
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-md text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: '#1B2A4A' }}
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-center pt-1">
            <a
              href="/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-[12px] underline"
              style={{ color: '#6B7280' }}
            >
              Forgot password?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
